/* ============================================================================
   Accessibility Fixes — Runtime DOM patches (self-healing)
   Loaded with `defer` from layout/theme.liquid. Runs on DOMContentLoaded
   + a 5-second sweep + MutationObserver for the lifetime of the page,
   because Slick + Shopify web-pixel + vendor scripts re-apply broken ARIA
   asynchronously after initial render.
   ============================================================================ */
(function () {
  'use strict';

  /* --------------------------------------------------------------------------
     Slick carousel: nuke ALL invalid ARIA Slick injects
     Slick's auto-generated ARIA is non-compliant with WAI-ARIA 1.2 and
     conflicts with native semantics. We aggressively strip every Slick
     ARIA artifact on slides + dots and use `inert` for inactive panes,
     which is the only modern way to remove focusable content from the
     a11y tree without screen-reader collision.
     -------------------------------------------------------------------------- */
  var SLICK_SLIDE_BAD_ATTRS = [
    'role', 'aria-hidden', 'aria-selected', 'aria-controls',
    'aria-describedby', 'aria-labelledby', 'tabindex'
  ];
  var SLICK_DOT_BAD_ATTRS = [
    'role', 'aria-hidden', 'aria-selected', 'aria-controls', 'tabindex'
  ];

  function normalizeSlickAria(root) {
    var scope = root || document;

    // Track + slides: strip every bad attr, use inert for inactive
    var tracks = scope.querySelectorAll('.slick-track');
    tracks.forEach(function (track) {
      track.removeAttribute('role');
    });

    var slides = scope.querySelectorAll('.slick-slide');
    slides.forEach(function (slide) {
      SLICK_SLIDE_BAD_ATTRS.forEach(function (attr) {
        slide.removeAttribute(attr);
      });
      // Inactive (and cloned) slides should not be in the tab order or
      // a11y tree at all. .slick-active is the visible slide.
      if (slide.classList.contains('slick-active') ||
          slide.classList.contains('slick-current')) {
        slide.removeAttribute('inert');
      } else {
        try { slide.setAttribute('inert', ''); } catch (e) {}
      }
    });

    // Dot list + dot items + dot buttons
    var dotLists = scope.querySelectorAll('.slick-dots');
    dotLists.forEach(function (dots) {
      dots.removeAttribute('role');
      var items = dots.querySelectorAll('li');
      items.forEach(function (li, idx) {
        SLICK_DOT_BAD_ATTRS.forEach(function (attr) {
          li.removeAttribute(attr);
        });
        var btn = li.querySelector('button');
        if (btn) {
          btn.removeAttribute('role');
          btn.removeAttribute('aria-controls');
          btn.removeAttribute('tabindex');
          btn.setAttribute('type', 'button');
          if (!btn.getAttribute('aria-label')) {
            btn.setAttribute('aria-label', 'Go to slide ' + (idx + 1));
          }
          if (li.classList.contains('slick-active')) {
            btn.setAttribute('aria-current', 'true');
          } else {
            btn.removeAttribute('aria-current');
          }
        }
      });
    });
  }

  /* --------------------------------------------------------------------------
     img elements with invalid ARIA roles (Lighthouse 4.1.2)
     <img> only supports role="img", "presentation", "none". Anything else
     (role="button", role="link", etc.) is invalid. Includes the logo image
     which sometimes inherits a role from a wrapping anchor's JS handler.
     -------------------------------------------------------------------------- */
  function fixImgRoles(root) {
    var scope = root || document;
    var imgs = scope.querySelectorAll('img[role]');
    var allowed = { 'img': 1, 'presentation': 1, 'none': 1 };
    imgs.forEach(function (img) {
      var role = (img.getAttribute('role') || '').toLowerCase();
      if (!allowed[role]) {
        img.removeAttribute('role');
      }
    });
  }

  /* --------------------------------------------------------------------------
     iframe title shim (Lighthouse 4.1.2)
     Shopify auto-injects iframes (web-pixel sandbox, embedded apps) with
     no title attribute. Add a generic title to any iframe missing one.
     -------------------------------------------------------------------------- */
  function fixIframeTitles(root) {
    var scope = root || document;
    var iframes = scope.querySelectorAll('iframe:not([title]), iframe[title=""]');
    iframes.forEach(function (frame) {
      var id = frame.id || '';
      var src = frame.getAttribute('src') || '';
      var label = 'Embedded content';
      if (id.indexOf('web-pixel') !== -1) label = 'Shopify analytics sandbox';
      else if (src.indexOf('youtube') !== -1) label = 'YouTube video';
      else if (src.indexOf('vimeo') !== -1) label = 'Vimeo video';
      else if (src.indexOf('google.com/maps') !== -1) label = 'Google Map';
      frame.setAttribute('title', label);
    });
  }

  /* --------------------------------------------------------------------------
     Duplicate header logo links (Lighthouse identical links)
     The theme renders both header-mobile and header-navigation-* sections,
     each with its own .header__heading-link logo anchor. Both point at
     home. Mark every duplicate after the first as aria-hidden + tabindex=-1
     so screen readers + Lighthouse only see one.
     -------------------------------------------------------------------------- */
  function dedupeHeaderLogoLinks(root) {
    var scope = root || document;
    var links = scope.querySelectorAll('a.header__heading-link');
    if (links.length <= 1) return;
    for (var i = 1; i < links.length; i++) {
      var link = links[i];
      if (link.getAttribute('aria-hidden') !== 'true') {
        link.setAttribute('aria-hidden', 'true');
      }
      if (link.getAttribute('tabindex') !== '-1') {
        link.setAttribute('tabindex', '-1');
      }
    }
  }

  /* --------------------------------------------------------------------------
     Stamped Reviews empty-link accessible name
     -------------------------------------------------------------------------- */
  function patchStampedAnchors(root) {
    var scope = root || document;
    var anchors = scope.querySelectorAll('a.stamped-reviews-link:not([aria-label]), a.stamped-style-color-link:not([aria-label])');
    anchors.forEach(function (a) {
      var text = (a.textContent || '').trim();
      if (!text) {
        a.setAttribute('aria-label', 'Read customer reviews');
      }
    });
  }

  /* --------------------------------------------------------------------------
     Empty anchor neutralization (PowerMapper F89)
     -------------------------------------------------------------------------- */
  function neutralizeEmptyAnchors(root) {
    var scope = root || document;
    var anchors = scope.querySelectorAll('a');
    anchors.forEach(function (a) {
      if (a.hasAttribute('data-allow-empty')) return;
      if (a.getAttribute('aria-hidden') === 'true') return;
      // Skip Slick + Slick-derived anchors (handled by normalizeSlickAria)
      if (a.closest('.slick-slider, .slick-list, .slick-dots, .header__heading')) return;
      var href = a.getAttribute('href');
      var hasUsefulHref = href && href.trim() !== '' && href.trim() !== '#';
      var text = (a.textContent || '').trim();
      var ariaLabel = (a.getAttribute('aria-label') || '').trim();
      var titleAttr = (a.getAttribute('title') || '').trim();
      var hasImgWithAlt = false;
      var img = a.querySelector('img[alt]');
      if (img) {
        hasImgWithAlt = (img.getAttribute('alt') || '').trim() !== '';
      }
      var hasName = text || ariaLabel || titleAttr || hasImgWithAlt;
      if (!hasUsefulHref && !hasName) {
        a.setAttribute('aria-hidden', 'true');
        a.setAttribute('tabindex', '-1');
      }
    });
  }

  /* --------------------------------------------------------------------------
     Master run-all
     -------------------------------------------------------------------------- */
  function runAll(root) {
    try { normalizeSlickAria(root); } catch (e) {}
    try { fixImgRoles(root); } catch (e) {}
    try { fixIframeTitles(root); } catch (e) {}
    try { dedupeHeaderLogoLinks(root); } catch (e) {}
    try { patchStampedAnchors(root); } catch (e) {}
    try { neutralizeEmptyAnchors(root); } catch (e) {}
  }

  /* --------------------------------------------------------------------------
     Throttled run + scheduler
     -------------------------------------------------------------------------- */
  var pending = false;
  function schedule() {
    if (pending) return;
    pending = true;
    if (typeof requestAnimationFrame !== 'undefined') {
      requestAnimationFrame(function () {
        pending = false;
        runAll(document);
      });
    } else {
      setTimeout(function () {
        pending = false;
        runAll(document);
      }, 16);
    }
  }

  /* --------------------------------------------------------------------------
     Boot sequence
     1) Run as soon as the document parses
     2) Run again on DOMContentLoaded
     3) Run again 250ms / 1s / 3s after load to catch async vendor injections
        (Stamped, iWish, Slick re-init, web-pixel sandbox)
     4) MutationObserver watches DOM AND attribute changes thereafter
     5) Hook jQuery Slick lifecycle events directly
     -------------------------------------------------------------------------- */
  runAll(document);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { runAll(document); });
  }

  function delayedSweep(ms) {
    setTimeout(function () { runAll(document); }, ms);
  }
  delayedSweep(250);
  delayedSweep(1000);
  delayedSweep(3000);
  delayedSweep(6000);

  if (typeof MutationObserver !== 'undefined') {
    var observer = new MutationObserver(function (mutations) {
      // Cheap heuristic: if any mutation touched slick / a / iframe / img, sweep
      for (var i = 0; i < mutations.length; i++) {
        var m = mutations[i];
        if (m.type === 'attributes') {
          var t = m.target;
          if (t && t.nodeType === 1) {
            var tag = t.tagName;
            if (tag === 'LI' || tag === 'UL' || tag === 'BUTTON' ||
                tag === 'A' || tag === 'IMG' || tag === 'IFRAME' ||
                (t.className && typeof t.className === 'string' &&
                 (t.className.indexOf('slick') !== -1 ||
                  t.className.indexOf('header__heading') !== -1))) {
              schedule();
              return;
            }
          }
        } else if (m.type === 'childList' && m.addedNodes.length) {
          schedule();
          return;
        }
      }
    });
    function startObserver() {
      observer.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: SLICK_SLIDE_BAD_ATTRS.concat(['title', 'href', 'class'])
      });
    }
    if (document.body) {
      startObserver();
    } else {
      document.addEventListener('DOMContentLoaded', startObserver);
    }
  }

  // jQuery Slick events
  function hookSlick() {
    if (typeof window.jQuery === 'undefined') return;
    var $ = window.jQuery;
    $(document).on('init reInit afterChange beforeChange setPosition breakpoint',
      '.slick-slider, .slick-initialized', function () {
        normalizeSlickAria(this);
      });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', hookSlick);
  } else {
    hookSlick();
  }
})();
